import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Star, Send, Loader2 } from "lucide-react";
import api from "@/lib/api";

function Stars({ value, onChange, size = "h-5 w-5" }) {
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type={onChange ? "button" : undefined}
          data-testid={onChange ? `star-${n}` : undefined}
          onClick={onChange ? () => onChange(n) : undefined}
          className={onChange ? "transition-transform hover:scale-110" : "cursor-default"}
        >
          <Star className={`${size} ${n <= value ? "fill-yellow-400 text-yellow-400" : "text-muted-foreground/40"}`} />
        </button>
      ))}
    </div>
  );
}

export default function Reviews() {
  const [reviews, setReviews] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", location: "", text: "" });
  const [rating, setRating] = useState(5);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/reviews");
      setReviews(data.reviews);
    } catch { toast.error("Couldn't load reviews. Please refresh."); }
  };
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/reviews", { ...form, rating });
      toast.success(data.message || "Thanks for your review!");
      setForm({ name: "", email: "", location: "", text: "" });
      setRating(5);
    } catch {
      toast.error("Could not submit review. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const avg = reviews.length ? (reviews.reduce((a, r) => a + r.rating, 0) / reviews.length).toFixed(1) : null;

  return (
    <div data-testid="reviews-page" className="pb-24">
      <div className="mx-auto max-w-3xl px-5 pt-20 text-center lg:px-8">
        <p className="text-sm font-semibold uppercase tracking-wider text-primary">Reviews</p>
        <h1 className="mt-3 font-heading text-4xl font-extrabold tracking-tighter md:text-5xl">What neighbors are saying</h1>
        {avg && (
          <div className="mt-5 flex items-center justify-center gap-2">
            <Stars value={Math.round(avg)} />
            <span className="font-heading text-lg font-bold">{avg}</span>
            <span className="text-muted-foreground">· {reviews.length} review{reviews.length !== 1 ? "s" : ""}</span>
          </div>
        )}
      </div>

      <div className="mx-auto mt-14 grid max-w-6xl gap-8 px-5 lg:grid-cols-3 lg:px-8">
        {/* Review list */}
        <div className="space-y-4 lg:col-span-2">
          {reviews.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-border py-16 text-center text-muted-foreground">
              Be the first to leave a review!
            </div>
          ) : (
            reviews.map((r, i) => (
              <div key={r.id || i} data-testid={`review-card-${i}`} className="rounded-2xl border border-border bg-card p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-heading font-bold">{r.name}</div>
                    {r.location && <div className="text-xs text-muted-foreground">{r.location}</div>}
                  </div>
                  <Stars value={r.rating} size="h-4 w-4" />
                </div>
                <p className="mt-3 text-muted-foreground">{r.text}</p>
              </div>
            ))
          )}
        </div>

        {/* Submit form */}
        <form onSubmit={submit} className="h-fit rounded-2xl border border-border bg-card p-6 lg:sticky lg:top-24">
          <h2 className="font-heading text-lg font-bold">Leave a review</h2>
          <p className="mt-1 text-sm text-muted-foreground">Reviews appear after a quick approval.</p>
          <div className="mt-4">
            <label className="text-sm font-semibold">Your rating</label>
            <div className="mt-1"><Stars value={rating} onChange={setRating} size="h-7 w-7" /></div>
          </div>
          <input data-testid="review-name" required placeholder="Name" value={form.name} onChange={set("name")}
            className="mt-3 w-full rounded-xl border border-border bg-muted/50 px-4 py-2.5 focus:bg-card focus:outline-none focus:ring-2 focus:ring-primary" />
          <input data-testid="review-email" type="email" required placeholder="Email (not shown publicly)" value={form.email} onChange={set("email")}
            className="mt-3 w-full rounded-xl border border-border bg-muted/50 px-4 py-2.5 focus:bg-card focus:outline-none focus:ring-2 focus:ring-primary" />
          <input data-testid="review-location" placeholder="Town (optional)" value={form.location} onChange={set("location")}
            className="mt-3 w-full rounded-xl border border-border bg-muted/50 px-4 py-2.5 focus:bg-card focus:outline-none focus:ring-2 focus:ring-primary" />
          <textarea data-testid="review-text" required rows={4} placeholder="How did we do?" value={form.text} onChange={set("text")}
            className="mt-3 w-full rounded-xl border border-border bg-muted/50 px-4 py-2.5 focus:bg-card focus:outline-none focus:ring-2 focus:ring-primary" />
          <button data-testid="review-submit" type="submit" disabled={busy}
            className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-6 py-3 font-bold text-primary-foreground transition-all hover:brightness-110 disabled:opacity-50">
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Send className="h-4 w-4" /> Submit Review</>}
          </button>
        </form>
      </div>
    </div>
  );
}
